import React, { useMemo, useEffect, useCallback, useState } from 'react'
import styles from './styles/common-competitors.module.scss'
import { useTranslation } from 'react-i18next'
import { Player } from 'types/global'
import { createDateFilterOptions } from 'components/generic/DateFilterBar'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { getCommonCompetitors, updateCommonCompetitorsTimeFilter } from 'actions/h2h-actions'
import WTNSpinner from 'components/generic/WTNSpinner'
import { TABLET_BREAKPOINT } from 'constants/global'
import useWindowSize from '../../hooks/use-windowSize'
import MobileFilter from 'components/generic/MobileFilterSelector/MobileFilterSelector'
import H2HCommonCompetitorsDesktop from './H2HCommonCompetitorsDesktop'
import H2HCommonCompetitorsMobile from './H2HCommonCompetitorsMobile'
import FilterDropdown from 'components/generic/FilterDropdown'
import { sortPlayersAlphabetically } from 'utils'

interface Props {
  players: [Player, Player]
}

const winPercent = (win, loss) => Math.round((win / (win + loss)) * 100) || 0

const H2HCommonCompetitors: React.FC<Props> = ({ players }) => {
  const { t } = useTranslation('strCommonCompetitors')

  const dispatch = useDispatch()
  const competitors = useSelector((state: AppState) => state.h2h.commonCompetitorsMatches)
  const loading = useSelector((state: AppState) => state.h2h.commonCompetitorsLoading)
  const timePeriodFilter = useSelector(
    (state: AppState) => state.h2h.commonCompetitorsTimePeriodFilter
  )
  const sortedCompetitors = useMemo(() => sortPlayersAlphabetically(competitors), [competitors])
  const getCommonCompetitorsMemo = useMemo(() => {
    const [p1Id, p2Id] = players.map(p => p.id)
    return (period: number | null) => dispatch(getCommonCompetitors([p1Id, p2Id], period))
  }, [dispatch, players])
  const updateTimeRange = useMemo(
    () => (timeRange: number | null) => dispatch(updateCommonCompetitorsTimeFilter(timeRange)),
    [dispatch]
  )
  const timePeriodOptions = useMemo(() => createDateFilterOptions([6, 12], true), [])

  const tableData = useMemo(
    () =>
      sortedCompetitors.map(c => ({
        ...c,
        p1WinPc: winPercent(c.p1Loss, c.p1Win),
        p2WinPc: winPercent(c.p2Loss, c.p2Win),
      })),
    [sortedCompetitors]
  )

  const totals = useMemo(() => {
    const [p1TotalWin, p1TotalLoss, p2TotalWin, p2TotalLoss] = tableData.reduce(
      (c, i) => {
        return [c[0] + i.p1Win, c[1] + i.p1Loss, c[2] + i.p2Win, c[3] + i.p2Loss]
      },
      [0, 0, 0, 0]
    )
    const totals = { p1TotalWin, p1TotalLoss, p2TotalWin, p2TotalLoss }
    return {
      ...totals,
      p1TotalWinPc: winPercent(p1TotalLoss, p1TotalWin),
      p2TotalWinPc: winPercent(p2TotalLoss, p2TotalWin),
    }
  }, [tableData])

  const [visibleCompetitorsInCommon, setVisibleCompetitorsInCommon] = useState(10)
  const windowWidth = useWindowSize().width

  useEffect(() => {
    getCommonCompetitorsMemo(timePeriodFilter)
  }, [getCommonCompetitorsMemo, timePeriodFilter])

  const changeTimeRange = useCallback(
    item => {
      updateTimeRange(item.key)
    },
    [updateTimeRange]
  )

  return (
    <div className={styles.commonCompetitors}>
      <h2>{t('commonCompetitors')}</h2>
      {windowWidth && windowWidth > TABLET_BREAKPOINT ? (
        <FilterDropdown
          options={timePeriodOptions}
          selectedKey={timePeriodFilter}
          onSelect={changeTimeRange}
        />
      ) : (
        <MobileFilter
          timePeriodFilterData={{
            timePeriodItems: timePeriodOptions,
            selectedTimePeriodItem: timePeriodFilter,
            updateTimePeriodFilterValue: changeTimeRange,
          }}
          classNames={{ filterBar: styles.filterBar }}
        />
      )}

      {loading ? (
        <div className={styles.textCenter}>
          <WTNSpinner width={85} />
        </div>
      ) : !competitors || !competitors.length ? (
        <NoCompetitors />
      ) : windowWidth && windowWidth > TABLET_BREAKPOINT ? (
        <H2HCommonCompetitorsDesktop
          players={players}
          tableData={tableData}
          visibleCompetitorsInCommon={visibleCompetitorsInCommon}
          setVisibleCompetitorsInCommon={setVisibleCompetitorsInCommon}
          totals={totals}
        />
      ) : (
        <H2HCommonCompetitorsMobile
          players={players}
          tableData={tableData}
          visibleCompetitorsInCommon={visibleCompetitorsInCommon}
          setVisibleCompetitorsInCommon={setVisibleCompetitorsInCommon}
          totals={totals}
        />
      )}
    </div>
  )
}

const NoCompetitors: React.FC = () => {
  const { t } = useTranslation('strCommonCompetitors')

  return (
    <div className={styles.textCenter}>
      <p className={styles.noDataText}>{t('noCommonCompetitors')}</p>
    </div>
  )
}

export default H2HCommonCompetitors
